import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type NotificationPreferences = {
  id?: string;
  user_id: string;
  notifications_enabled: boolean;
  group_messages: boolean;
  direct_messages: boolean;
  tournaments: boolean;
  polls: boolean;
  announcements: boolean;
  score_reminders: boolean;
  created_at?: string;
  updated_at?: string;
} | null;

const defaultPrefsFor = (userId: string) => ({
  user_id: userId,
  notifications_enabled: true,
  group_messages: true,
  direct_messages: true,
  tournaments: true,
  polls: true,
  announcements: true,
  score_reminders: true,
});

export function useNotificationPreferences() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    enabled: !!user,
    queryKey: ['notification-preferences', user?.id],
    queryFn: async (): Promise<NotificationPreferences> => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('user_notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        // Return defaults without creating a row yet
        return defaultPrefsFor(user.id) as NotificationPreferences;
      }
      return data as NotificationPreferences;
    },
  });

  const mutation = useMutation({
    mutationFn: async (partial: Partial<NotificationPreferences>) => {
      if (!user) throw new Error('Not authenticated');
      // Try upsert behavior: if row exists, update; else insert defaults merged with partial
      const current = query.data ?? defaultPrefsFor(user.id);
      const payload = { ...current, ...partial, user_id: user.id } as any;

      // Check if exists in DB
      const { data: exists } = await supabase
        .from('user_notification_preferences')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (exists?.id) {
        const { error } = await supabase
          .from('user_notification_preferences')
          .update(payload)
          .eq('user_id', user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_notification_preferences')
          .insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['notification-preferences', user?.id] });
    },
  });

  return {
    prefs: query.data,
    isLoading: query.isLoading,
    updatePreferences: mutation.mutateAsync,
  };
}
